import {
  type NWTEvent,
  useTracking as useTrackingNWT,
} from '@xing-com/crate-communication-tracking';

import { TakenCard, AvailableCard, MemberCard } from '../../types';

export type TrackParams = {
  itemUrn?: string;
  trackingToken?: string;
  status: 'TakenJob' | 'AvailableJob' | 'Member';
  cardType: 'job_reco';
  position: number;
};

type trackErrorShownParams = {
  element: string;
  context: string;
  position: number;
};

export type trackTileInteractionParams = {
  tileName: 'skills' | 'salary' | 'commonalities' | 'company';
  status: 'TakenJob' | 'AvailableJob' | 'Tutorial';
  itemUrn?: string;
  trackingToken?: string;
};

type UseTrackingReturn = {
  trackLike: (params: TrackParams) => void;
  trackSuperLike: (params: TrackParams) => void;
  trackDislike: (params: TrackParams) => void;
  trackOpenJobAd: (params: TrackParams) => void;
  trackTileInteraction: (params: trackTileInteractionParams) => void;
  trackFirstTakenJobTooltip: (params: TrackParams) => void;
  trackCloseFirstTakenJobTooltip: (params: TrackParams) => void;
  trackErrorShown: (params: trackErrorShownParams) => void;
  trackDeleteBookmark: ({ itemUrn }: { itemUrn: string }) => void;
  trackYourJobsOpenAvailableJob: ({
    itemUrn,
    trackingToken,
  }: {
    itemUrn: string;
    trackingToken: string;
  }) => void;
  trackDeleteSearchAlert: ({ itemUrn }: { itemUrn: string }) => void;
};

const page = 'jobs/index';

export const getTrackingParams = (
  card: TakenCard | AvailableCard | MemberCard,
  deckIndex: number
): TrackParams => {
  const { urn, trackingToken } = card.data;

  return {
    itemUrn: urn || '',
    trackingToken: trackingToken || '',
    status: card.type,
    cardType: 'job_reco',
    position: deckIndex,
  };
};

export function useJobCardTracking(): UseTrackingReturn {
  const { track: trackNwt } = useTrackingNWT<NWTEvent>();

  const trackLike = ({
    itemUrn,
    trackingToken,
    status,
    position,
  }: TrackParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'liked',
      eventSchema: 'basic',
      element: 'job_reco_card',
      elementDetail: 'dream_job_reco',
      context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
      sentBy: 'jobs',
      page,
      itemUrn,
      trackingToken,
      position,
    });
  };
  const trackDislike = ({
    itemUrn,
    trackingToken,
    status,
    position,
  }: TrackParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'discarded',
      eventSchema: 'basic',
      element: 'job_reco_card',
      elementDetail: 'dream_job_reco',
      sentBy: 'jobs',
      page,
      context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
      itemUrn,
      trackingToken,
      position,
    });
  };
  const trackSuperLike = ({
    itemUrn,
    trackingToken,
    status,
    position,
  }: TrackParams): void => {
    if (status === 'AvailableJob') {
      trackNwt({
        type: 'nwt',
        event: 'bookmarked',
        eventSchema: 'basic',
        element: 'job_posting',
        elementDetail: 'dream_job_reco',
        context: `dream_job_status_available`,
        sentBy: 'jobs',
        page,
        itemUrn,
        trackingToken,
        position,
      });
    } else {
      trackNwt({
        type: 'nwt',
        event: 'activated',
        eventSchema: 'basic',
        element: 'search_alert',
        elementDetail: 'dream_job_reco',
        context: `dream_job_status_taken`,
        sentBy: 'jobs',
        page,
        itemUrn,
        trackingToken,
        position,
      });
    }
  };

  const trackErrorShown = ({
    element,
    context,
    position,
  }: trackErrorShownParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'error_shown',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page,
      element,
      context,
      position,
    });
  };

  // https://new-work.atlassian.net/browse/UIC-9225 item 8
  const trackOpenJobAd = ({
    itemUrn,
    trackingToken,
    position,
  }: TrackParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page,
      element: 'job_ad',
      elementDetail: 'dream_job_reco',
      context: `dream_job_status_available`,
      itemUrn,
      trackingToken,
      position,
    });
  };

  const trackTileInteraction = ({
    tileName,
    status,
    itemUrn,
    trackingToken,
  }: trackTileInteractionParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'clicked',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page,
      element: `${tileName}_tile`,
      elementDetail: 'dream_job_reco',
      itemUrn,
      trackingToken,
      context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
    });
  };

  const trackFirstTakenJobTooltip = ({
    itemUrn,
    trackingToken,
    position,
  }: TrackParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'viewed',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page,
      element: 'occupied_tooltip',
      position,
      itemUrn,
      trackingToken,
    });
  };

  const trackCloseFirstTakenJobTooltip = ({
    itemUrn,
    trackingToken,
    position,
  }: TrackParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'clicked',
      eventSchema: 'basic',
      sentBy: 'jobs',
      element: 'occupied_tooltip_close',
      page,
      position,
      itemUrn,
      trackingToken,
    });
  };

  // https://new-work.atlassian.net/browse/UIC-9225 item 14
  const trackDeleteBookmark = ({ itemUrn }: { itemUrn: string }): void => {
    trackNwt({
      type: 'nwt',
      event: 'unbookmarked',
      eventSchema: 'basic',
      element: 'job_posting',
      elementDetail: 'your_jobs',
      context: `dream_job_status_available`,
      sentBy: 'jobs',
      page,
      itemUrn,
    });
  };

  // https://new-work.atlassian.net/browse/UIC-9225 item 15
  const trackYourJobsOpenAvailableJob = ({
    itemUrn,
    trackingToken,
  }: {
    itemUrn: string;
    trackingToken: string;
  }): void => {
    trackNwt({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      element: 'job_ad',
      elementDetail: 'your_jobs',
      context: `dream_job_status_available`,
      sentBy: 'jobs',
      page,
      itemUrn,
      trackingToken,
    });
  };

  // https://new-work.atlassian.net/browse/UIC-9225 item 16
  const trackDeleteSearchAlert = ({ itemUrn }: { itemUrn: string }): void => {
    trackNwt({
      type: 'nwt',
      event: 'deactivated',
      eventSchema: 'basic',
      element: 'search_alert',
      elementDetail: 'dream_job_reco',
      context: `dream_job_status_taken`,
      sentBy: 'jobs',
      page,
      itemUrn,
    });
  };

  return {
    trackLike,
    trackSuperLike,
    trackDislike,
    trackOpenJobAd,
    trackTileInteraction,
    trackFirstTakenJobTooltip,
    trackCloseFirstTakenJobTooltip,
    trackErrorShown,
    trackDeleteBookmark,
    trackYourJobsOpenAvailableJob,
    trackDeleteSearchAlert,
  };
}
